import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import {
  ClipboardCheck, Search, CheckCircle, AlertCircle, XCircle, Loader2,
  FileSearch, Plus, Minus, Camera, Upload, X, FileText, Eye, Edit2, Trash2,
  Package, Briefcase, ChevronUp, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { canUserEdit, canUserDelete } from '@/utils/permissions';
import AttachmentGrid from '@/components/AttachmentGrid';

const STATUS_COLORS = {
  'Pending': 'bg-amber-100 text-amber-800',
  'Passed': 'bg-emerald-100 text-emerald-800',
  'Approved': 'bg-emerald-100 text-emerald-800',
  'Failed': 'bg-rose-100 text-rose-800',
  'Rejected': 'bg-rose-100 text-rose-800',
};

const TEST_TYPES = [
  'Chemical Analysis',
  'Purity Test',
  'Visual Inspection',
  'Weight Verification',
  'Dimensional Check',
];

const blankInspectionItem = () => ({
  material: '',
  invoiceNumber: '',  // auto-captured from GRN
  result: 'Pending',
  remarks: '',
});

export default function ProjectQA({ poType: defaultPoType }) {
  const { API, user } = useContext(AppContext);
  const canEdit = canUserEdit(user, '/projects/qa');
  const canDelete = canUserDelete(user, '/projects/qa');
  const pageLabel = 'Project QA';

  const [qas, setQas] = useState([]);
  const [pendingGRNs, setPendingGRNs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [poTypeFilter, setPoTypeFilter] = useState(defaultPoType || 'all');
  const [actioningId, setActioningId] = useState(null);
  // Expanded card state — single QA can be expanded at a time (mirrors Stores QA)
  const [expandedQA, setExpandedQA] = useState(null);
  // Full-details modal (mirrors Stores GRN eye-icon view but for QA)
  const [viewQA, setViewQA] = useState(null);
  // Edit modal
  const [editQA, setEditQA] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  // Delete confirmation
  const [deletingQA, setDeletingQA] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const openEdit = (q) => {
    setEditQA(q);
    setEditForm({
      // Collapse legacy "Pass"/"Fail" to the new vocabulary so the dropdown
      // displays a clean current value. Internal backend logic already
      // treats Pass==Approved and Fail==Rejected as equivalent.
      status: (q.status === 'Pass' || q.status === 'Approved') ? 'Approved'
            : (q.status === 'Fail' || q.status === 'Rejected') ? 'Rejected'
            : 'Pending',
      inspector: q.inspector || '',
      testType: q.testType || '',
      remarks: q.remarks || '',
    });
  };

  const submitEdit = async () => {
    if (!editQA) return;
    setSavingEdit(true);
    try {
      // 1) Status change — query-param PUT runs the side-effects pipeline
      //    (payment recompute, auto-rejection, admin-reversal guard).
      const prevStatus = (editQA.status === 'Pass') ? 'Approved'
                       : (editQA.status === 'Fail') ? 'Rejected'
                       : (editQA.status || 'Pending');
      if (editForm.status && editForm.status !== prevStatus) {
        await axios.put(`${API}/projects/qa/${editQA.id}`, null, {
          params: { status: editForm.status, remarks: editForm.remarks || '' },
        });
      }
      // 2) Metadata fields (testType, inspector, remarks) go through the
      //    /images body endpoint — always sent so remarks-only edits save.
      await axios.put(`${API}/projects/qa/${editQA.id}/images`, {
        testType: editForm.testType || '',
        inspector: editForm.inspector || '',
        remarks: editForm.remarks || '',
      });
      toast.success('QA record updated');
      setEditQA(null);
      await fetchQAs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update QA');
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingQA) return;
    setDeletingId(deletingQA.id);
    try {
      await axios.delete(`${API}/projects/qa/${deletingQA.id}`);
      toast.success('QA record deleted');
      setDeletingQA(null);
      await fetchQAs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete QA');
    } finally {
      setDeletingId(null);
    }
  };

  // New QA Check modal state (mirrors Stores QA)
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [testType, setTestType] = useState('');
  const [qaItems, setQaItems] = useState([blankInspectionItem()]);
  const [capturedImages, setCapturedImages] = useState([]);
  const [saveStatus, setSaveStatus] = useState(null);
  const fileInputRef = useRef(null);

  const fetchQAs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects/qa?poType=${poTypeFilter}`);
      setQas(r.data || []);
    } catch (e) {
      toast.error('Failed to load Project QA');
    } finally {
      setLoading(false);
    }
  }, [API, poTypeFilter]);

  const fetchPendingGRNs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/grns-pending-qa?poType=all`);
      setPendingGRNs(r.data || []);
    } catch (e) {
      console.error(e);
    }
  }, [API]);

  useEffect(() => {
    fetchQAs();
    fetchPendingGRNs();
  }, [fetchQAs, fetchPendingGRNs]);

  const handleAction = async (qaId, action) => {
    setActioningId(qaId);
    try {
      const status = action === 'pass' ? 'Approved' : 'Rejected';
      await axios.put(`${API}/projects/qa/${qaId}?status=${status}`);
      toast.success(`QA ${status}`);
      await fetchQAs();
      await fetchPendingGRNs();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} QA`);
    } finally {
      setActioningId(null);
    }
  };

  // ---- New QA modal handlers ----
  const handleGRNSelect = (grn) => {
    setSelectedGRN(grn);
    // Pre-fill items from GRN — one row per commodity. Mirrors Stores QA.
    const grnItems = Array.isArray(grn?.items) && grn.items.length > 0 ? grn.items : null;
    if (grnItems) {
      setQaItems(grnItems.map(it => ({
        material: it.commodityName || it.material || '',
        invoiceNumber: grn.invoiceNo || grn.invoiceNumber || '',
        testType: testType || '',
        result: 'Pending',
        remarks: '',
      })));
    } else {
      setQaItems([{
        material: grn?.material || '',
        invoiceNumber: grn?.invoiceNo || grn?.invoiceNumber || '',
        testType: testType || '',
        result: 'Pending',
        remarks: '',
      }]);
    }
  };

  const addInspectionItem = () => {
    if (testType !== 'Visual Inspection') return;
    setQaItems(prev => [...prev, {
      material: '',
      invoiceNumber: selectedGRN?.invoiceNo || selectedGRN?.invoiceNumber || '',
      result: 'Pending',
      remarks: '',
    }]);
  };

  const removeInspectionItem = (idx) => {
    if (qaItems.length <= 1) return;
    setQaItems(prev => prev.filter((_, i) => i !== idx));
  };

  const updateInspectionItem = (idx, field, value) => {
    setQaItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setCapturedImages(prev => [...prev, ev.target.result]);
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const resetForm = () => {
    setSelectedGRN(null);
    setTestType('');
    setQaItems([blankInspectionItem()]);
    setCapturedImages([]);
    setSaveStatus(null);
  };

  const handleSubmitQA = async () => {
    if (!selectedGRN) {
      toast.error('Select a GRN first');
      return;
    }
    if (qaItems.some(it => !it.material.trim())) {
      toast.error('Every inspection item needs a Material name');
      return;
    }
    if (qaItems.some(it => !it.testType)) {
      toast.error('Pick a Test Type for every inspection item');
      return;
    }
    setSaveStatus('saving');
    const inspector = user?.name || user?.fullName || user?.email || 'Current User';
    try {
      for (const item of qaItems) {
        // Create QA record
        const r = await axios.post(
          `${API}/projects/qa`,
          null,
          {
            params: {
              grnNo: selectedGRN.id,
              inspector,
              result: item.result || 'Pending',
              commodityName: item.material,
            },
          },
        );
        const qaId = r.data.id;
        // If user picked Approved/Rejected directly, fire the status PUT so the
        // backend creates stock lots + payment row (mirrors Stores QA flow).
        if (item.result === 'Approved' || item.result === 'Rejected' || item.result === 'Passed' || item.result === 'Failed') {
          await axios.put(
            `${API}/projects/qa/${qaId}`,
            null,
            { params: { status: item.result, remarks: item.remarks || '' } },
          );
        }
        // Always upload metadata + photos in a single PUT to the new
        // /qa/{id}/images endpoint (matches Stores QA contract). Sends each
        // item's OWN testType so multi-item GRNs can have different test
        // types per commodity.
        await axios.put(`${API}/projects/qa/${qaId}/images`, {
          images: capturedImages,
          testType: item.testType,
          remarks: item.remarks || '',
          batchNo: item.invoiceNumber || '',
        });
      }
      await fetchQAs();
      await fetchPendingGRNs();
      setSaveStatus('saved');
      toast.success(`${qaItems.length} QA record${qaItems.length === 1 ? '' : 's'} created`);
      setTimeout(() => {
        setShowAddModal(false);
        resetForm();
      }, 800);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to submit QA';
      toast.error(msg);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 2500);
    }
  };

  const filtered = qas.filter(q => {
    const matchesSearch = !searchTerm ||
      q.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.material?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.grnNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.supplier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.inspector?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.invoiceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.poNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || q.status === statusFilter;
    const matchesPoType = poTypeFilter === 'all' || (q.poType || 'material') === poTypeFilter;
    return matchesSearch && matchesStatus && matchesPoType;
  });

  const pendingCount = qas.filter(q => q.status === 'Pending').length;

  // Stats — mirrors the Stores QA top-cards layout.
  const stats = {
    total: qas.length,
    approved: qas.filter(q => q.status === 'Approved' || q.status === 'Passed').length,
    rejected: qas.filter(q => q.status === 'Rejected' || q.status === 'Failed').length,
    pending: qas.filter(q => q.status === 'Pending' || !q.status).length,
  };

  // Normalize legacy Pass/Fail → Approved/Rejected for display.
  const _normStatus = (s) => {
    if (s === 'Passed' || s === 'Approved') return 'Approved';
    if (s === 'Failed' || s === 'Rejected') return 'Rejected';
    return 'Pending';
  };
  const getResultColor = (result) => {
    const r = _normStatus(result);
    if (r === 'Approved') return 'bg-green-100 text-green-700 border-green-200';
    if (r === 'Rejected') return 'bg-red-100 text-red-700 border-red-200';
    return 'bg-orange-100 text-orange-700 border-orange-200';
  };
  const getResultIcon = (result) => {
    const r = _normStatus(result);
    if (r === 'Approved') return <CheckCircle size={14} />;
    if (r === 'Rejected') return <AlertCircle size={14} />;
    return null;
  };

  return (
    <div className="space-y-6" data-testid="project-qa-page">
      {/* Header — matches Stores QA layout (title + subtitle + primary CTA) */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quality Assurance (QA)</h1>
          <p className="text-gray-500 mt-1">Inspect project materials and services · upload quality proof photos</p>
        </div>
        <button
          onClick={() => { setShowAddModal(true); resetForm(); }}
          className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md"
          data-testid="project-qa-new-btn"
        >
          <Plus size={18} /><span>New QA Check</span>
        </button>
      </div>

      {/* Stats — same 4-card grid as Stores QA */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <p className="text-sm text-gray-500">Total Checks</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center space-x-2"><CheckCircle className="text-green-500" size={18} /><p className="text-sm text-green-600 font-medium">Approved</p></div>
          <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-5 border border-red-200">
          <div className="flex items-center space-x-2"><AlertCircle className="text-red-500" size={18} /><p className="text-sm text-red-600 font-medium">Rejected</p></div>
          <p className="text-2xl font-bold text-red-700">{stats.rejected}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-200">
          <p className="text-sm text-orange-600 font-medium">Pending</p>
          <p className="text-2xl font-bold text-orange-700">{stats.pending}</p>
        </div>
      </div>

      {/* Material QA / Service QA tabs */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex">
          <button
            onClick={() => setPoTypeFilter('material')}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold transition border-b-2 ${
              poTypeFilter === 'material' || poTypeFilter === 'all'
                ? 'border-amber-500 text-amber-700 bg-amber-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="project-material-qa-tab"
          >
            <Package size={18} /> Material QA
          </button>
          <button
            onClick={() => setPoTypeFilter('service')}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold transition border-b-2 ${
              poTypeFilter === 'service'
                ? 'border-indigo-500 text-indigo-700 bg-indigo-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="project-service-qa-tab"
          >
            <Briefcase size={18} /> Service QA
          </button>
        </div>
      </div>

      {/* Pending GRNs info banner */}
      {pendingGRNs.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
          <ClipboardCheck className="text-blue-500" size={24} />
          <div>
            <p className="text-sm font-medium text-blue-800">{pendingGRNs.length} GRN(s) Pending QA</p>
            <p className="text-xs text-blue-600">Click &quot;New QA Check&quot; to start quality inspection</p>
          </div>
        </div>
      )}

      {/* Search bar */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by QA ID, GRN No, Material, Supplier, Inspector..."
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-[#1a237e] outline-none bg-white shadow-sm"
          data-testid="project-qa-search"
        />
      </div>

      {/* QA Cards */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
            <Loader2 className="animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
            <FileSearch className="mx-auto mb-2 text-gray-300" size={36} />
            No Project QA records found. Click &quot;New QA Check&quot; to start an inspection.
          </div>
        ) : filtered.map(qa => (
          <div key={qa.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Card header — clickable for expand/collapse */}
            <div
              className="flex items-center justify-between px-5 py-3.5 cursor-pointer hover:bg-gray-50/60 transition"
              onClick={() => setExpandedQA(expandedQA === qa.id ? null : qa.id)}
              data-testid={`project-qa-card-${qa.id}`}
            >
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="flex-shrink-0">
                  <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium border ${getResultColor(qa.status)}`}>
                    {getResultIcon(qa.status)}<span>{_normStatus(qa.status)}</span>
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-[#1a237e]">{qa.id}</span>
                    <span className="text-gray-300">|</span>
                    <span className="text-sm font-medium text-gray-900 truncate">{qa.material}</span>
                    {qa.poType === 'service' && (
                      <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[10px] font-medium uppercase">Service</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                    <span>GRN: {qa.grnNo}</span>
                    <span>{qa.supplier || '-'}</span>
                    <span>{qa.date}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                {/* Inline Approve/Reject for Pending — convenience action */}
                {(qa.status === 'Pending' || !qa.status) && canEdit && (
                  <>
                    <button
                      onClick={() => handleAction(qa.id, 'pass')}
                      disabled={actioningId === qa.id}
                      className="p-1.5 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-lg disabled:opacity-60"
                      title="Approve"
                      data-testid={`project-qa-approve-${qa.id}`}
                    >
                      {actioningId === qa.id ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                    </button>
                    <button
                      onClick={() => handleAction(qa.id, 'fail')}
                      disabled={actioningId === qa.id}
                      className="p-1.5 bg-rose-100 text-rose-700 hover:bg-rose-200 rounded-lg disabled:opacity-60"
                      title="Reject"
                      data-testid={`project-qa-reject-${qa.id}`}
                    >
                      <XCircle size={16} />
                    </button>
                  </>
                )}
                <button
                  onClick={() => setViewQA(qa)}
                  className="p-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg"
                  title="View QA Details"
                  data-testid={`project-qa-view-${qa.id}`}
                >
                  <Eye size={16} />
                </button>
                {canEdit && (
                  <button
                    onClick={() => openEdit(qa)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-medium hover:bg-blue-100 transition"
                    title="Edit QA"
                    data-testid={`project-qa-edit-${qa.id}`}
                  >
                    <Edit2 size={14} />
                    {qa.status === 'Pending' ? 'Review' : 'Edit'}
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => setDeletingQA(qa)}
                    className="p-1.5 bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-lg"
                    title="Delete QA"
                    data-testid={`project-qa-delete-${qa.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
                {expandedQA === qa.id
                  ? <ChevronUp size={18} className="text-gray-400" />
                  : <ChevronDown size={18} className="text-gray-400" />}
              </div>
            </div>

            {/* Expanded details */}
            {expandedQA === qa.id && (
              <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/30 space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div><p className="text-xs text-gray-500">QA ID</p><p className="text-sm font-medium text-gray-900">{qa.id}</p></div>
                  <div><p className="text-xs text-gray-500">GRN Number</p><p className="text-sm font-medium text-gray-900">{qa.grnNo}</p></div>
                  <div><p className="text-xs text-gray-500">PO Number</p><p className="text-sm font-medium text-gray-900">{qa.poNumber || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Invoice No</p><p className="text-sm font-medium text-gray-900">{qa.invoiceNo || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Material</p><p className="text-sm font-medium text-gray-900">{qa.material}</p></div>
                  <div><p className="text-xs text-gray-500">Supplier</p><p className="text-sm font-medium text-gray-900">{qa.supplier || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Quantity</p><p className="text-sm font-medium text-gray-900">{qa.quantity ? `${qa.quantity} ${qa.unit || ''}` : '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Rate / Amount</p><p className="text-sm font-medium text-gray-900">{qa.rate ? `Rs. ${qa.rate}` : '-'} / {qa.amount ? `Rs. ${qa.amount}` : '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Inspector</p><p className="text-sm font-medium text-gray-900">{qa.inspector || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Date</p><p className="text-sm font-medium text-gray-900">{qa.date}</p></div>
                  <div><p className="text-xs text-gray-500">Test Type</p><p className="text-sm font-medium text-gray-900">{qa.testType || '-'}</p></div>
                  <div><p className="text-xs text-gray-500">Type</p><p className="text-sm font-medium text-gray-900">{qa.poType === 'service' ? 'Service' : 'Material'}</p></div>
                </div>
                {qa.remarks && (
                  <div className="pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Remarks</p>
                    <p className="text-sm text-gray-700">{qa.remarks}</p>
                  </div>
                )}
                {(qa.images?.length > 0 || qa.documents?.length > 0) && (
                  <div className="pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">QA Documents / Photos</p>
                    <AttachmentGrid attachments={qa.images || qa.documents || []} />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* NEW QA CHECK MODAL — mirrors Stores QA card */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">New Project QA Check</h2>
              <button onClick={() => { setShowAddModal(false); resetForm(); }}
                className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            {/* GRN Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select GRN (Pending QA) <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedGRN?.id || ''}
                onChange={(e) => {
                  const grn = pendingGRNs.find(g => g.id === e.target.value);
                  if (grn) handleGRNSelect(grn);
                  else setSelectedGRN(null);
                }}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                data-testid="project-qa-grn-select"
              >
                <option value="">— Select a GRN pending QA —</option>
                {pendingGRNs.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.id} — {g.vendorName} (Inv: {g.invoiceNo})
                  </option>
                ))}
              </select>
              {pendingGRNs.length === 0 && (
                <p className="text-xs text-amber-700 mt-1">No GRNs awaiting QA.</p>
              )}
              {selectedGRN && (
                <div className="mt-2 p-3 bg-blue-50 rounded-lg text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-gray-500">Supplier:</span> <span className="font-medium">{selectedGRN.vendorName || selectedGRN.supplier}</span></div>
                    <div><span className="text-gray-500">Project:</span> <span className="font-medium">{selectedGRN.projectName || '-'}</span></div>
                    <div><span className="text-gray-500">PO:</span> <span className="font-medium">{selectedGRN.poNumber || '-'}</span></div>
                    <div><span className="text-gray-500">Items:</span> <span className="font-medium">{selectedGRN.items?.length || 0}</span></div>
                  </div>
                </div>
              )}
            </div>

            {/* Default Test Type (quick-fill all items) */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Test Type
                <span className="text-xs text-gray-400 font-normal ml-2">
                  (fills empty items below — each item can override)
                </span>
              </label>
              <select
                value={testType}
                onChange={(e) => {
                  const t = e.target.value;
                  setTestType(t);
                  if (t) {
                    setQaItems(prev => prev.map(it => ({
                      ...it,
                      testType: it.testType || t,
                    })));
                  }
                }}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                data-testid="project-qa-test-type-select"
              >
                <option value="">— None (set each item below) —</option>
                {TEST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              {selectedGRN && qaItems.length > 1 && (
                <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                  <CheckCircle size={12} /> Multi-item GRN — pick the test type for each commodity individually below
                </p>
              )}
            </div>

            {/* Inspection Items */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">Inspection Items</h3>
                {selectedGRN && qaItems.length > 1 && (
                  <span className="text-xs text-gray-500">
                    {qaItems.length} items in GRN — decide each independently
                  </span>
                )}
              </div>

              <div className="space-y-4">
                {qaItems.map((item, idx) => (
                  <div key={idx} className="p-4 border border-gray-200 rounded-xl bg-gray-50"
                    data-testid={`project-qa-item-${idx}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-600">Item {idx + 1}</span>
                      {qaItems.length > 1 && !(selectedGRN?.items?.length > 0) && (
                        <button
                          type="button"
                          onClick={() => removeInspectionItem(idx)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Minus size={16} />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Material <span className="text-red-500">*</span></label>
                        {/* If GRN has items, expose them as dropdown options to keep parity tight */}
                        {selectedGRN && selectedGRN.items && selectedGRN.items.length > 0 ? (
                          <select
                            value={item.material}
                            onChange={(e) => updateInspectionItem(idx, 'material', e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                            data-testid={`project-qa-material-${idx}`}
                          >
                            <option value="">— Select commodity —</option>
                            {selectedGRN.items.map((g, gi) => (
                              <option key={gi} value={g.commodityName}>
                                {g.commodityName} ({g.receivedQty || g.netWeight || g.quantity} {g.uom || g.unit || ''})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={item.material}
                            onChange={(e) => updateInspectionItem(idx, 'material', e.target.value)}
                            placeholder="Material name"
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                            data-testid={`project-qa-material-${idx}`}
                          />
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                        <input
                          type="text"
                          value={item.invoiceNumber}
                          readOnly
                          placeholder="Auto-captured from GRN"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50"
                          data-testid={`project-qa-invoice-${idx}`}
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Test Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={item.testType || ''}
                          onChange={(e) => updateInspectionItem(idx, 'testType', e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-white"
                          data-testid={`project-qa-test-type-${idx}`}
                        >
                          <option value="">Select test type</option>
                          {TEST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Result</label>
                        <select
                          value={item.result}
                          onChange={(e) => updateInspectionItem(idx, 'result', e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                          data-testid={`project-qa-result-${idx}`}
                        >
                          <option value="Pending">Pending</option>
                          <option value="Approved">Approved</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                        <input
                          type="text"
                          value={item.remarks}
                          onChange={(e) => updateInspectionItem(idx, 'remarks', e.target.value)}
                          placeholder="Optional"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                          data-testid={`project-qa-remarks-${idx}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Photos & Documents */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                <Camera size={18} className="text-emerald-600" />
                <span>Photos & Documents ({capturedImages.length})</span>
                <span className="text-xs font-normal text-gray-400">— images, PDFs, Word & Excel</span>
              </h3>

              {capturedImages.length === 0 ? (
                <div className="bg-gray-900 rounded-xl aspect-video relative overflow-hidden mb-4">
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                    <Camera size={48} />
                    <p className="mt-2 text-sm">Upload inspection photos OR supporting documents (PDF / Word / Excel)</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {capturedImages.map((img, idx) => {
                    const isImg = typeof img === 'string' && img.startsWith('data:image');
                    // Try to detect mime/ext from the data-URL prefix so the tile
                    // shows the right label (PDF / DOCX / XLSX / IMG).
                    const mimeMatch = typeof img === 'string' ? img.match(/^data:([^;]+);/) : null;
                    const mime = mimeMatch ? mimeMatch[1] : '';
                    const tag =
                      mime.includes('pdf') ? 'PDF' :
                      mime.includes('word') ? 'DOC' :
                      mime.includes('sheet') || mime.includes('excel') ? 'XLS' :
                      mime.startsWith('image') ? 'IMG' : 'FILE';
                    return (
                      <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                        {isImg ? (
                          <img src={img} alt={`Proof ${idx + 1}`} className="w-full h-full object-cover" />
                        ) : (
                          <a
                            href={img}
                            download={`attachment-${idx + 1}`}
                            className="flex flex-col items-center justify-center h-full p-2 text-center hover:bg-gray-100 transition"
                            title="Click to download / preview"
                          >
                            <FileText size={24} className="text-emerald-600 mb-1" />
                            <span className="text-[10px] font-semibold text-gray-700">{tag}</span>
                            <span className="text-[10px] text-gray-500">File {idx + 1}</span>
                          </a>
                        )}
                        <button
                          onClick={() => setCapturedImages(prev => prev.filter((_, i) => i !== idx))}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 bg-gray-100 text-gray-700 py-2.5 px-4 rounded-xl font-medium flex items-center justify-center space-x-2 hover:bg-gray-200 transition"
                  data-testid="project-qa-upload-btn"
                >
                  <Upload size={18} /><span>Upload Files</span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                />
                {capturedImages.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setCapturedImages([])}
                    className="bg-red-50 text-red-600 py-2.5 px-4 rounded-xl font-medium text-sm"
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitQA}
                disabled={!selectedGRN || qaItems.some(i => !i.material || !i.testType) || saveStatus === 'saving'}
                className={`px-4 py-2.5 rounded-xl font-medium flex items-center gap-2 ${
                  saveStatus === 'saved'
                    ? 'bg-green-500 text-white'
                    : saveStatus === 'error'
                    ? 'bg-red-500 text-white'
                    : 'bg-emerald-600 text-white disabled:opacity-50 hover:bg-emerald-700'
                }`}
                data-testid="project-qa-submit-btn"
              >
                {saveStatus === 'saving' && <Loader2 size={16} className="animate-spin" />}
                {saveStatus === 'saving' ? 'Saving…' : (saveStatus === 'saved' ? 'Saved' : 'Create QA')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QA Details Modal — mirrors Stores GRN eye-icon view for QA records */}
      {viewQA && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="project-qa-view-modal">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <Eye className="text-blue-500" size={20} />
                <h2 className="font-bold text-lg">QA Details - {viewQA.id}</h2>
                <span className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase ${
                  viewQA.poType === 'service' ? 'bg-indigo-100 text-indigo-700' : 'bg-teal-100 text-teal-700'
                }`}>
                  {viewQA.poType === 'service' ? 'Service' : 'Material'}
                </span>
              </div>
              <button onClick={() => setViewQA(null)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Status */}
              <div className="flex justify-between items-center">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[viewQA.status] || 'bg-gray-100 text-gray-800'}`}>
                  {viewQA.status}
                </span>
                <span className="text-xs text-gray-500">Date: {viewQA.date || '-'}</span>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 text-xs">Linked GRN</p>
                  <p className="font-medium font-mono text-blue-600">{viewQA.grnNo || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Supplier</p>
                  <p className="font-medium">{viewQA.supplier || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Inspector</p>
                  <p className="font-medium">{viewQA.inspector || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Test Type</p>
                  <p className="font-medium">{viewQA.testType || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500 text-xs">{viewQA.poType === 'service' ? 'Service' : 'Material'}</p>
                  <p className="font-medium">{viewQA.material || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Quantity</p>
                  <p className="font-bold text-blue-600 text-lg">{viewQA.quantity || 0} {viewQA.unit || ''}</p>
                </div>
                {viewQA.invoiceNumber && (
                  <div>
                    <p className="text-gray-500 text-xs">Invoice Number</p>
                    <p className="font-medium text-blue-600">{viewQA.invoiceNumber}</p>
                  </div>
                )}
              </div>

              {/* Inspection Items (multi-item QA) */}
              {Array.isArray(viewQA.items) && viewQA.items.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <ClipboardCheck size={16} className="text-emerald-500" />
                    Inspection Items ({viewQA.items.length})
                  </h3>
                  <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">#</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">{viewQA.poType === 'service' ? 'Service' : 'Material'}</th>
                          <th className="px-3 py-2 text-center font-semibold text-gray-700">Result</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Remarks</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {viewQA.items.map((item, idx) => (
                          <tr key={idx} className="bg-white">
                            <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                            <td className="px-3 py-2 font-medium">{item.material || item.commodityName || '-'}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[item.result] || 'bg-gray-100 text-gray-700'}`}>
                                {item.result || '-'}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-gray-600">{item.remarks || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Photos / Documents */}
              {(() => {
                const attachments = viewQA.images || viewQA.documents || viewQA.photos || [];
                if (!Array.isArray(attachments) || attachments.length === 0) return null;
                return (
                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                      Photos / Documents ({attachments.length})
                    </h3>
                    <AttachmentGrid attachments={attachments} />
                  </div>
                );
              })()}

              {/* Remarks */}
              {viewQA.remarks && (
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-gray-500 text-xs">Remarks</p>
                  <p className="text-sm">{viewQA.remarks}</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setViewQA(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm"
                data-testid="project-qa-view-close"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit QA Modal */}
      {editQA && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="project-qa-edit-modal">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Edit2 size={18} className="text-amber-600" />
                <h2 className="font-bold text-lg">Edit QA — {editQA.id}</h2>
              </div>
              <button onClick={() => setEditQA(null)} className="p-1 hover:bg-gray-100 rounded">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 uppercase">Status / Result</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm(p => ({ ...p, status: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                    data-testid="edit-qa-status"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 uppercase">Inspector</label>
                  <input
                    type="text"
                    value={editForm.inspector}
                    onChange={(e) => setEditForm(p => ({ ...p, inspector: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    data-testid="edit-qa-inspector"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-medium text-gray-600 uppercase">Test Type</label>
                  <select
                    value={editForm.testType}
                    onChange={(e) => setEditForm(p => ({ ...p, testType: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                    data-testid="edit-qa-testtype"
                  >
                    <option value="">-- Select Test Type --</option>
                    <option value="Chemical Analysis">Chemical Analysis</option>
                    <option value="Purity Test">Purity Test</option>
                    <option value="Visual Inspection">Visual Inspection</option>
                    <option value="Weight Verification">Weight Verification</option>
                    <option value="Dimensional Check">Dimensional Check</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase">Remarks</label>
                <textarea
                  rows={3}
                  value={editForm.remarks}
                  onChange={(e) => setEditForm(p => ({ ...p, remarks: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="edit-qa-remarks"
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setEditQA(null)}
                disabled={savingEdit}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={submitEdit}
                disabled={savingEdit}
                className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="edit-qa-save"
              >
                {savingEdit ? <Loader2 size={14} className="animate-spin" /> : <Edit2 size={14} />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete QA confirmation */}
      {deletingQA && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="project-qa-delete-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-5">
            <div className="flex items-center gap-2 mb-3">
              <Trash2 size={18} className="text-rose-600" />
              <h2 className="font-bold text-lg">Delete QA — {deletingQA.id}?</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              This will permanently remove the QA record. Linked stock lots & payments are unaffected.
              <br />
              Material: <strong>{deletingQA.material || '-'}</strong>
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeletingQA(null)}
                disabled={!!deletingId}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={!!deletingId}
                className="px-4 py-2 text-sm bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="confirm-qa-delete-btn"
              >
                {deletingId ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Delete QA
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
